import React, { useEffect, useMemo } from 'react';
import { FiltersLoading, OpenFilters } from 'src/components/FiltersSlider/comps';
import { FModal } from 'src/components/FiltersSlider/FModal';
import { useFiltersState, ModalType, FiltersType } from './state';
import { useStateSelector } from 'src/redux';
import { ContentUsers } from './ContentUsers';
import { ContentEvents } from './ContentEvents';
import { FilterObjectKey, useFiltersForQuery } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { isQueryLoading } from 'src/graphql/client';
import { FilterOption, FiltersStructure } from 'src/components/FiltersSlider/types';
import _ from 'lodash';

const Filters: React.FC = () => {
  const { modalType, setModalType, setFilters, filters, setIsInit, isInit } = useFiltersState();
  const colors = useStateSelector(({ theme }) => theme.colors);
  const { workspaceId } = useAuth();

  const { data, loading, networkStatus } = useFiltersForQuery({
    variables: {
      workspaceId: workspaceId,
      object: FilterObjectKey.Controls,
      includeCounts: false,
    },
  });
  const isLoading = isQueryLoading({ loading, data, networkStatus });

  useEffect(() => {
    if (data?.filtersFor?.controls) {
      const omit = <T extends { __typename?: string }>(el: T) => _.omit(el, ['__typename']);

      const cleanedFilters: FiltersType = {
        strategy: data.filtersFor.controls.strategy.map(omit),
        status: data.filtersFor.controls.status.map(omit),
        owners: data.filtersFor.controls.owners.map(omit),
        frameworks: data.filtersFor.controls.frameworks.map(omit),
        assessments: data.filtersFor.controls.assessments.map(omit),
        existingMaturity: data.filtersFor.controls.existingMaturity.map(omit),
        targetMaturity: data.filtersFor.controls.targetMaturity.map(omit),
      };
      setFilters(cleanedFilters);
      setIsInit(true);
    }
  }, [data]);

  const content: FiltersStructure<ModalType>[] = useMemo(
    () => [
      {
        type: 'assessments',
        title: 'Assessment',
        component: (
          <ContentEvents
            onClose={() => setModalType(null)}
            filters={filters.assessments}
            setFilters={(assessments) => setFilters({ ...filters, assessments })}
            title="Assessment"
          />
        ),
      },
      {
        type: 'strategy',
        title: 'Strategy',
        component: (
          <ContentEvents
            onClose={() => setModalType(null)}
            filters={filters.strategy}
            setFilters={(strategy) => setFilters({ ...filters, strategy })}
            title="Strategy"
          />
        ),
      },
      {
        type: 'owners',
        title: 'Owner',
        component: (
          <ContentUsers
            onClose={() => setModalType(null)}
            filters={filters.owners}
            setFilters={(owners) => setFilters({ ...filters, owners })}
            title="Owner"
          />
        ),
      },
      {
        type: 'frameworks',
        title: 'Framework',
        component: (
          <ContentEvents
            onClose={() => setModalType(null)}
            filters={filters.frameworks}
            setFilters={(frameworks) => setFilters({ ...filters, frameworks })}
            title="Framework"
          />
        ),
      },
      {
        type: 'status',
        title: 'Status',
        component: (
          <ContentEvents
            onClose={() => setModalType(null)}
            filters={filters.status}
            setFilters={(status) => setFilters({ ...filters, status })}
            title="Status"
          />
        ),
      },
      {
        type: 'existingMaturity',
        title: 'Existing Maturity',
        component: (
          <ContentEvents
            onClose={() => setModalType(null)}
            filters={filters.existingMaturity}
            setFilters={(existingMaturity) => setFilters({ ...filters, existingMaturity })}
            title="Existing Maturity"
          />
        ),
      },
      {
        type: 'targetMaturity',
        title: 'Target Maturity',
        component: (
          <ContentEvents
            onClose={() => setModalType(null)}
            filters={filters.targetMaturity}
            setFilters={(targetMaturity) => setFilters({ ...filters, targetMaturity })}
            title="Target Maturity"
          />
        ),
      },
    ],
    [colors, filters],
  );

  const handleRestore = () => {
    const enable = <T extends FilterOption>(item: T) => ({ ...item, enabled: true });

    const restoredFilters: FiltersType = {
      strategy: filters.strategy.map(enable),
      status: filters.status.map(enable),
      owners: filters.owners.map(enable),
      frameworks: filters.frameworks.map(enable),
      assessments: filters.assessments.map(enable),
      existingMaturity: filters.existingMaturity.map(enable),
      targetMaturity: filters.targetMaturity.map(enable),
    };

    setFilters(restoredFilters);
  };

  if (!isInit || isLoading) return <FiltersLoading />;

  return (
    <>
      <FModal<ModalType> modalType={modalType} setModalType={setModalType} content={content} />
      <OpenFilters<ModalType>
        setModalType={setModalType}
        handleRestore={handleRestore}
        content={content}
        bgColor="white"
      />
    </>
  );
};

export default Filters;
